import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Plus, Calendar as CalendarIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';

function LeaveManagement({ user }) {
  const [leaves, setLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    leave_type: 'vacation'
  });

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      const myLeavesRes = await authFetch(`${API}/leave/my-requests`);
      const myLeaves = await myLeavesRes.json();
      setLeaves(myLeaves);

      if (user.role === 'admin') {
        const allLeavesRes = await authFetch(`${API}/leave/all`);
        const allLeavesData = await allLeavesRes.json();
        setAllLeaves(allLeavesData);
      }
    } catch (error) {
      console.error('Error fetching leaves:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await authFetch(`${API}/leave/request`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Leave request submitted!');
        setDialogOpen(false);
        setFormData({ start_date: '', end_date: '', reason: '', leave_type: 'vacation' });
        fetchLeaves();
      } else {
        toast.error('Failed to submit leave request');
      }
    } catch (error) {
      toast.error('Failed to submit leave request');
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/approve`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave approved');
        fetchLeaves();
      } else {
        toast.error('Failed to approve leave');
      }
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleReject = async (leaveId) => {
    try {
      const response = await authFetch(`${API}/leave/${leaveId}/reject`, {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Leave rejected');
        fetchLeaves();
      } else {
        toast.error('Failed to reject leave');
      }
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <p className="text-foreground-muted">Loading...</p>
        </div>
      </Layout>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-accent/10 text-accent';
      case 'rejected':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  return (
    <Layout user={user}>
      <div data-testid="leave-page">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT">Leave Management</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="request-leave-btn" className="bg-primary hover:bg-primary-hover rounded-full">
                <Plus className="w-4 h-4 mr-2" />
                Request Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-heading font-bold">Request Leave</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="start_date">Start Date</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    className="rounded-lg"
                    data-testid="leave-start-date"
                  />
                </div>
                <div>
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    required
                    className="rounded-lg"
                    data-testid="leave-end-date"
                  />
                </div>
                <div>
                  <Label htmlFor="leave_type">Leave Type</Label>
                  <Select
                    value={formData.leave_type}
                    onValueChange={(value) => setFormData({ ...formData, leave_type: value })}
                  >
                    <SelectTrigger className="rounded-lg" data-testid="leave-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vacation">Vacation</SelectItem>
                      <SelectItem value="sick">Sick Leave</SelectItem>
                      <SelectItem value="personal">Personal</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="reason">Reason</Label>
                  <Input
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    required
                    className="rounded-lg"
                    placeholder="Brief reason for leave"
                    data-testid="leave-reason"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary-hover rounded-full" data-testid="submit-leave-btn">
                  Submit Request
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
            <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-4">My Leave Requests</h2>
            {leaves.length === 0 ? (
              <div className="text-center py-8">
                <CalendarIcon className="w-12 h-12 text-foreground-muted mx-auto mb-4" />
                <p className="text-foreground-muted">No leave requests yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leaves.map((leave) => (
                  <div
                    key={leave.leave_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-xl gap-4"
                    data-testid={`leave-${leave.leave_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-foreground-DEFAULT capitalize">{leave.leave_type}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(leave.status)}`}>
                          {leave.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground-muted mb-1">
                        {leave.start_date} to {leave.end_date}
                      </p>
                      <p className="text-sm text-foreground-DEFAULT">{leave.reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {user.role === 'admin' && allLeaves.length > 0 && (
            <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-6">
              <h2 className="text-xl font-heading font-bold text-foreground-DEFAULT mb-4">All Leave Requests</h2>
              <div className="space-y-3">
                {allLeaves.map((leave) => (
                  <div
                    key={leave.leave_id}
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-slate-50 rounded-xl gap-4"
                    data-testid={`admin-leave-${leave.leave_id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-foreground-DEFAULT capitalize">{leave.leave_type}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(leave.status)}`}>
                          {leave.status}
                        </span>
                      </div>
                      <p className="text-sm text-foreground-muted mb-1">
                        {leave.start_date} to {leave.end_date}
                      </p>
                      <p className="text-sm text-foreground-DEFAULT">{leave.reason}</p>
                    </div>
                    {leave.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleApprove(leave.leave_id)}
                          size="sm"
                          className="bg-accent hover:bg-accent/90 rounded-full"
                          data-testid={`approve-leave-${leave.leave_id}`}
                        >
                          Approve
                        </Button>
                        <Button
                          onClick={() => handleReject(leave.leave_id)}
                          size="sm"
                          variant="destructive"
                          className="rounded-full"
                          data-testid={`reject-leave-${leave.leave_id}`}
                        >
                          Reject
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default LeaveManagement;